import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "@/lib/api";

const STATUS_COLORS = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-500",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-500",
  REJECTED: "bg-slate-100 text-slate-600 border-slate-300",
  FAILED: "bg-rose-50 text-rose-700 border-rose-500",
};

export default function PendingApprovals() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [tab, setTab] = useState("PENDING");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals", { params: { status: tab } });
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (r, kind) => {
    const reason = kind === "reject" ? window.prompt(t("collab.approval_reject_prompt", "Reason for rejecting (optional):")) : null;
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const { data } = await api.post(`/approvals/${r.id}/${kind}`, { reason });
      if (kind === "approve") {
        toast[data.ok ? "success" : "error"](data.outcome?.detail || t("collab.approval_approved", "Approved"));
      } else {
        toast.success(t("collab.approval_rejected", "Rejected"));
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [r.id]: false })); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Approvals</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("collab.approvals_title", "Approval requests")}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("collab.approvals_hint", "Sensitive actions requested by collaborators. Approve or reject each request.")}</p>
      </div>

      <div className="flex items-center gap-1">
        {[["PENDING", Clock], ["APPROVED", CheckCircle2], ["REJECTED", XCircle], ["FAILED", XCircle]].map(([k, Icon]) => (
          <button key={k} data-testid={`approvals-tab-${k}`} onClick={() => setTab(k)} className={"inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold " + (tab === k ? "bg-[#0F62FE] text-white border-[#0F62FE]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")}>
            <Icon className="w-3.5 h-3.5" /> {k}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        {loading ? (
          <div className="px-4 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-6 text-center text-slate-500">{t("collab.approvals_empty", "No requests in this tab.")}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`approval-row-${r.id}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={"text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border " + (STATUS_COLORS[r.status] || "border-slate-300")}>{r.status}</span>
                    <div className="font-semibold text-slate-900">{r.action}</div>
                    {r.payload?.amount_usd != null && <div className="text-sm text-slate-600">${Number(r.payload.amount_usd).toFixed(2)} USD</div>}
                  </div>
                  <div className="text-xs text-slate-600">
                    {t("collab.approval_by", "Requested by")} <span className="font-semibold">{r.requester_username}</span>
                    {r.payload?.target_username && <> · {t("collab.approval_target", "for")} <span className="font-semibold">{r.payload.target_username}</span></>}
                    <> · {new Date(r.created_at).toLocaleString()}</>
                  </div>
                  {r.reason && <div className="text-xs text-slate-500 italic">"{r.reason}"</div>}
                  {r.resolution_reason && <div className="text-xs text-slate-500">{t("collab.approval_resolution", "Admin note")}: {r.resolution_reason}</div>}
                  {r.execution_result?.detail && <div className={"text-xs " + (r.execution_result.ok ? "text-emerald-700" : "text-rose-700")}>{r.execution_result.detail}</div>}
                </div>
                {r.status === "PENDING" && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button data-testid={`approval-approve-${r.id}`} disabled={busy[r.id]} onClick={() => act(r, "approve")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
                      {busy[r.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} {t("common.approve", "Approve")}
                    </button>
                    <button data-testid={`approval-reject-${r.id}`} disabled={busy[r.id]} onClick={() => act(r, "reject")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-50 disabled:opacity-50">
                      <XCircle className="w-3.5 h-3.5" /> {t("common.reject", "Reject")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
